import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Plus, Trash2, ArrowLeft, Save, ChevronUp, ChevronDown, Upload, Download, FileSpreadsheet } from 'lucide-react';

const emptyCourse = {
  title: '', description: '', thumbnail_url: '', passing_score: 80,
  availability_days: 7, has_evaluation: true,
  slides: [], questions: []
};

export default function CourseEditor() {
  const navigate = useNavigate();
  const { courseId } = useParams();
  const isNew = !courseId || courseId === 'new';
  const [course, setCourse] = useState(emptyCourse);
  const [loading, setLoading] = useState(false);
  // Importación de preguntas desde Excel
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    if (!isNew) {
      api.getCourse(courseId).then((r) => {
        setCourse({
          title: r.data.title || '',
          description: r.data.description || '',
          thumbnail_url: r.data.thumbnail_url || '',
          passing_score: r.data.passing_score || 80,
          availability_days: r.data.availability_days || 7,
          has_evaluation: r.data.has_evaluation !== false,
          slides: r.data.slides || [],
          questions: r.data.questions || [],
        });
      }).catch(() => toast.error('Error al cargar curso'));
    }
  }, [courseId, isNew]);

  const addSlide = () => {
    setCourse({ ...course, slides: [...course.slides, { order: course.slides.length + 1, content_url: '', audio_url: '', slide_type: 'presentation' }] });
  };

  const updateSlide = (i, key, val) => {
    const slides = [...course.slides];
    slides[i] = { ...slides[i], [key]: val };
    setCourse({ ...course, slides });
  };

  const moveSlide = (i, dir) => {
    const slides = [...course.slides];
    const j = i + dir;
    if (j < 0 || j >= slides.length) return;
    [slides[i], slides[j]] = [slides[j], slides[i]];
    slides.forEach((s, k) => s.order = k + 1);
    setCourse({ ...course, slides });
  };

  const removeSlide = (i) => {
    const slides = course.slides.filter((_, k) => k !== i);
    slides.forEach((s, k) => s.order = k + 1);
    setCourse({ ...course, slides });
  };

  const addQuestion = () => {
    setCourse({ ...course, questions: [...course.questions, { question_text: '', question_type: 'multiple_choice', options: ['', '', '', ''], correct_answer: 0 }] });
  };

  const updateQuestion = (i, key, val) => {
    const questions = [...course.questions];
    questions[i] = { ...questions[i], [key]: val };
    if (key === 'question_type' && val === 'true_false') {
      questions[i].options = ['Verdadero', 'Falso'];
      questions[i].correct_answer = 0;
    } else if (key === 'question_type' && val === 'multiple_choice') {
      questions[i].options = ['', '', '', ''];
      questions[i].correct_answer = 0;
    }
    setCourse({ ...course, questions });
  };

  const updateOption = (qi, oi, val) => {
    const questions = [...course.questions];
    questions[qi].options[oi] = val;
    setCourse({ ...course, questions });
  };

  const removeQuestion = (i) => {
    setCourse({ ...course, questions: course.questions.filter((_, k) => k !== i) });
  };

  const handleDownloadQuestionsTemplate = async () => {
    try {
      const res = await api.downloadQuestionsTemplate();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_preguntas.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar la plantilla'); }
  };

  const handleImportQuestions = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await api.parseQuestionsExcel(fd);
      setImportResult(res.data);
      if (res.data.questions.length > 0) {
        setCourse((prev) => ({ ...prev, questions: [...prev.questions, ...res.data.questions] }));
        toast.success(`${res.data.imported} pregunta(s) agregada(s) — recuerda Guardar el curso`);
      }
      if (res.data.failed > 0) toast.warning(`${res.data.failed} fila(s) con error — revisa el detalle`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al procesar el archivo');
    } finally { setImportLoading(false); }
  };

  const handleSave = async () => {
    if (!course.title || !course.description) { toast.error('Título y descripción requeridos'); return; }
    setLoading(true);
    try {
      const payload = {
        title: course.title,
        description: course.description,
        thumbnail_url: course.thumbnail_url || null,
        passing_score: parseInt(course.passing_score) || 80,
        availability_days: Math.min(365, Math.max(1, parseInt(course.availability_days) || 7)),
        has_evaluation: course.has_evaluation,
        slides: course.slides,
        questions: course.has_evaluation ? course.questions : [],
      };
      if (isNew) {
        await api.createCourse(payload);
        toast.success('Curso creado');
      } else {
        await api.updateCourse(courseId, payload);
        toast.success('Curso actualizado');
      }
      navigate('/admin/courses');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-5xl" data-testid="course-editor-page">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/admin/courses')} className="flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" /> Volver
          </Button>
          <Button onClick={handleSave} disabled={loading} data-testid="save-course-button" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white flex items-center gap-2">
            <Save className="h-4 w-4" /> {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-6 uppercase">{isNew ? 'Nuevo Curso' : 'Editar Curso'}</h1>

        {/* Datos generales */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 space-y-4">
          <div>
            <Label className="text-sm font-medium uppercase tracking-wide">Título *</Label>
            <Input data-testid="course-title-input" value={course.title} onChange={(e) => setCourse({ ...course, title: e.target.value })} className="h-11" />
          </div>
          <div>
            <Label className="text-sm font-medium uppercase tracking-wide">Descripción *</Label>
            <textarea data-testid="course-desc-input" value={course.description} onChange={(e) => setCourse({ ...course, description: e.target.value })} className="w-full p-3 border border-gray-300 rounded-md h-24" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium uppercase tracking-wide">URL miniatura</Label>
              <Input data-testid="course-thumb-input" value={course.thumbnail_url} onChange={(e) => setCourse({ ...course, thumbnail_url: e.target.value })} placeholder="https://drive.google.com/file/d/..." className="h-11" />
              <p className="text-xs text-gray-500 mt-1">
                Para Drive: comparte el archivo como <strong>"Cualquier persona con el enlace"</strong> y pega la URL aquí. Se generará la miniatura automáticamente.
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium uppercase tracking-wide">Puntaje aprobación (%)</Label>
              <Input data-testid="course-passing-input" type="number" min="0" max="100" value={course.passing_score} onChange={(e) => setCourse({ ...course, passing_score: e.target.value })} className="h-11" disabled={!course.has_evaluation} />
              {!course.has_evaluation && <p className="text-xs text-amber-600 mt-1">No aplica: curso sin evaluación.</p>}
            </div>
            <div>
              <Label className="text-sm font-medium uppercase tracking-wide">Días disponibles (1-365)</Label>
              <Input
                data-testid="course-availability-input"
                type="number"
                min="1"
                max="365"
                value={course.availability_days}
                onChange={(e) => setCourse({ ...course, availability_days: e.target.value })}
                className="h-11"
              />
              <p className="text-xs text-gray-500 mt-1">
                Días que el usuario tendrá disponible el curso desde que se le asigne. Pasado este periodo el curso se deshabilita.
              </p>
            </div>
          </div>
          <div className={`border rounded-lg p-4 flex items-start gap-3 ${!course.has_evaluation ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}>
            <input
              type="checkbox"
              id="no-eval-checkbox"
              data-testid="course-no-evaluation-checkbox"
              checked={!course.has_evaluation}
              onChange={(e) => setCourse({ ...course, has_evaluation: !e.target.checked })}
              className="h-5 w-5 mt-0.5 accent-[#DC143C] cursor-pointer"
            />
            <label htmlFor="no-eval-checkbox" className="cursor-pointer">
              <span className="text-sm font-medium uppercase tracking-wide text-gray-900 block">Curso sin evaluación (informativo)</span>
              <span className="text-xs text-gray-600 block mt-1">
                {course.has_evaluation
                  ? 'Si lo activas, el curso no tendrá preguntas ni puntaje. Al terminar de ver el contenido se marcará como completado automáticamente (sin diploma).'
                  : 'El usuario verá el mensaje "Este curso es informativo no tiene evaluación" y el curso se completará al ver todo el contenido (sin puntaje ni diploma).'}
              </span>
            </label>
          </div>
        </div>

        {/* Slides */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 uppercase">Diapositivas / Videos</h2>
            <Button onClick={addSlide} data-testid="add-slide-button" variant="outline" size="sm" className="flex items-center gap-1"><Plus className="h-4 w-4" />Agregar</Button>
          </div>
          {course.slides.length === 0 ? (
            <p className="text-center text-gray-400 py-6">Sin diapositivas</p>
          ) : (
            <div className="space-y-3">
              {course.slides.map((s, i) => (
                <div key={i} className="border border-gray-200 rounded p-4 bg-gray-50" data-testid="slide-item">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium">Slide #{s.order}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => moveSlide(i, -1)}><ChevronUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => moveSlide(i, 1)}><ChevronDown className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => removeSlide(i)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs uppercase">Tipo</Label>
                      <Select value={s.slide_type} onValueChange={(v) => updateSlide(i, 'slide_type', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="presentation">Presentación / PDF</SelectItem>
                          <SelectItem value="image">Imagen</SelectItem>
                          <SelectItem value="video">Video</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase">URL contenido</Label>
                      <Input value={s.content_url} onChange={(e) => updateSlide(i, 'content_url', e.target.value)} placeholder="https://drive.google.com/file/d/..." />
                    </div>
                    <div>
                      <Label className="text-xs uppercase">URL audio (opcional)</Label>
                      <Input value={s.audio_url || ''} onChange={(e) => updateSlide(i, 'audio_url', e.target.value)} placeholder="https://..." />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preguntas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 uppercase">Preguntas de Evaluación</h2>
            {course.has_evaluation && (
              <div className="flex items-center gap-2">
                <Button onClick={() => { setImportOpen(true); setImportFile(null); setImportResult(null); }} data-testid="import-questions-button" variant="outline" size="sm" className="flex items-center gap-1 border-[#DC143C] text-[#DC143C] hover:bg-red-50">
                  <Upload className="h-4 w-4" />Importar Excel
                </Button>
                <Button onClick={addQuestion} data-testid="add-question-button" variant="outline" size="sm" className="flex items-center gap-1"><Plus className="h-4 w-4" />Agregar</Button>
              </div>
            )}
          </div>
          {!course.has_evaluation ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center text-sm text-amber-800" data-testid="no-evaluation-notice">
              Este curso está marcado como <strong>informativo</strong>: no tiene evaluación.
              El usuario verá el mensaje "Este curso es informativo no tiene evaluación" y se completará al ver todo el contenido.
            </div>
          ) : course.questions.length === 0 ? (
            <p className="text-center text-gray-400 py-6">Sin preguntas</p>
          ) : (
            <div className="space-y-3">
              {course.questions.map((q, qi) => (
                <div key={qi} className="border border-gray-200 rounded p-4 bg-gray-50" data-testid="question-item">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium">Pregunta #{qi + 1}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeQuestion(qi)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase">Texto pregunta</Label>
                      <Input value={q.question_text} onChange={(e) => updateQuestion(qi, 'question_text', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs uppercase">Tipo</Label>
                      <Select value={q.question_type} onValueChange={(v) => updateQuestion(qi, 'question_type', v)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="multiple_choice">Opción múltiple</SelectItem>
                          <SelectItem value="true_false">Verdadero / Falso</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {q.options.map((o, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={q.correct_answer === oi}
                          onChange={() => updateQuestion(qi, 'correct_answer', oi)}
                          className="h-4 w-4"
                          disabled={q.question_type === 'true_false'}
                        />
                        <Input
                          value={o}
                          onChange={(e) => updateOption(qi, oi, e.target.value)}
                          placeholder={`Opción ${oi + 1}`}
                          disabled={q.question_type === 'true_false'}
                        />
                      </div>
                    ))}
                    {q.question_type === 'true_false' && (
                      <div className="flex gap-4 text-sm">
                        <label className="flex items-center gap-2">
                          <input type="radio" checked={q.correct_answer === 0} onChange={() => updateQuestion(qi, 'correct_answer', 0)} /> Verdadero
                        </label>
                        <label className="flex items-center gap-2">
                          <input type="radio" checked={q.correct_answer === 1} onChange={() => updateQuestion(qi, 'correct_answer', 1)} /> Falso
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Dialog: Importar preguntas desde Excel */}
        <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportFile(null); setImportResult(null); } }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="import-questions-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold uppercase tracking-tight flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-[#DC143C]" />
                Importar preguntas desde Excel
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
                <p className="font-semibold">¿Cómo funciona?</p>
                <ol className="list-decimal ml-4 space-y-1 text-blue-800">
                  <li>Descarga la plantilla y llena una fila por pregunta (la hoja "Instrucciones" tiene ejemplos).</li>
                  <li><strong>Opción múltiple:</strong> 2 a 4 opciones (A-D) y en RESPUESTA CORRECTA la letra (A, B, C o D).</li>
                  <li><strong>Verdadero/Falso:</strong> deja las opciones vacías y escribe VERDADERO o FALSO en la respuesta.</li>
                  <li>Las preguntas se <strong>agregan</strong> a las existentes y podrás editarlas antes de Guardar.</li>
                </ol>
              </div>
              <Button variant="outline" onClick={handleDownloadQuestionsTemplate} data-testid="download-questions-template" className="w-full h-11 flex items-center justify-center gap-2">
                <Download className="h-4 w-4" /> Descargar plantilla Excel
              </Button>
              <div>
                <Label className="text-sm font-medium uppercase tracking-wide mb-1.5 block">Archivo Excel (.xlsx) *</Label>
                <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="questions-file-input" className="h-11 pt-2" />
              </div>
              {importResult && (
                <div className="space-y-2" data-testid="import-questions-result">
                  <div className="grid grid-cols-2 gap-3 text-center">
                    <div className="bg-green-50 border border-green-200 rounded p-2">
                      <p className="text-xl font-bold text-green-700" data-testid="questions-imported-count">{importResult.imported}</p>
                      <p className="text-xs uppercase text-green-800">Importadas</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded p-2">
                      <p className="text-xl font-bold text-red-700" data-testid="questions-failed-count">{importResult.failed}</p>
                      <p className="text-xs uppercase text-red-800">Con error</p>
                    </div>
                  </div>
                  {importResult.errors.length > 0 && (
                    <div className="border border-red-200 rounded max-h-40 overflow-y-auto bg-red-50 p-2 text-xs text-red-800 space-y-1">
                      {importResult.errors.map((e) => (
                        <p key={e.row} data-testid="question-error-row">Fila {e.row}: {e.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setImportOpen(false)}>{importResult ? 'Cerrar' : 'Cancelar'}</Button>
                <Button onClick={handleImportQuestions} disabled={!importFile || importLoading} data-testid="import-questions-submit" className="bg-[#DC143C] hover:bg-[#B91C1C] text-white">
                  {importLoading ? 'Procesando...' : 'Importar preguntas'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
